<script setup>
  import {computed, onBeforeUnmount, ref} from "vue";

  import AiResultCard from "@components/_DevOnly/AiResultCard.vue";
  import AiStatusBadge from "@components/_DevOnly/AiStatusBadge.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentSwitch from "@components/Switch.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {aiProviders, getModelSettings} from "@enums/aiEnums";
  import {
    MedButton,
    MedCard,
    MedCardContent,
    MedCardHeader,
    MedCheckbox,
    MedFormField,
    MedLabel,
  } from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "PagesDevAiMassDebugger",
  });

  const props = defineProps({
    features: {
      type: Array,
      default: () => [],
    },
    targets: {
      type: Array,
      default: () => [],
    },
  });

  // Constants
  const urlParameters = new URLSearchParams(globalThis.location.search);

  // Refs: Context
  const id = ref(urlParameters.get("id") || "");
  const feature = ref(urlParameters.get("feature") || "");
  const target = ref(urlParameters.get("target") || "");

  // Refs: Prompt & Configuration
  const systemPrompt = ref("");
  const prompt = ref("");
  const placeholders = ref([]);
  const technicalTermsJson = ref("{}");
  const schemaJson = ref("{}");
  const replaceTechnicalTerms = ref(false);
  const enableSchema = ref(false);
  const contextLoaded = ref(false);

  // Refs: Model Selection
  const selectedModels = ref([]);
  const collapsedProviders = ref({});

  // Refs: Parameter Sets
  const parameterSets = ref([getDefaultParameterSet()]);

  // Refs: Iterations & Cache
  const iterations = ref(1);
  const disableCache = ref(false);
  const disableFailedCache = ref(false);

  // Refs: Score & Flag Expressions
  const scoreExpression = ref("return result.response && result.response.length > 0 ? 10 : 0;");
  const scoreExpressionError = ref(null);
  const maxScore = ref(10);
  const redFlagExpression = ref("return false;");
  const redFlagExpressionError = ref(null);
  const yellowFlagExpression = ref("return false;");
  const yellowFlagExpressionError = ref(null);

  const scoreEvaluator = createExpressionEvaluator(scoreExpression, scoreExpressionError, null);
  const evaluateScore = scoreEvaluator.evaluate;
  const testScoreExpression = scoreEvaluator.test;

  const redFlagEvaluator = createExpressionEvaluator(redFlagExpression, redFlagExpressionError, false);
  const evaluateRedFlag = redFlagEvaluator.evaluate;
  const testRedFlagExpression = redFlagEvaluator.test;

  const yellowFlagEvaluator = createExpressionEvaluator(yellowFlagExpression, yellowFlagExpressionError, false);
  const evaluateYellowFlag = yellowFlagEvaluator.evaluate;
  const testYellowFlagExpression = yellowFlagEvaluator.test;

  // Refs: Runs & Status
  const runs = ref([]);
  const isRunning = ref(false);
  const selectedRunIndex = ref(null);
  const globalError = ref(null);

  // Refs: Abort
  const isAbortingAll = ref(false);
  const abortAllResult = ref(null);

  // Refs: Timer
  const timerStart = ref(null);
  const timerEnd = ref(null);
  const elapsedSeconds = ref(0);

  // Refs: History
  const history = ref([]);
  const historyLoading = ref(false);

  // Interval Variables
  let pollInterval = null;
  let elapsedInterval = null;

  // Computed: Options
  const featureOptions = computed(() => props.features.map((f) => ({key: f, label: f})));
  const targetOptions = computed(() => props.targets.map((t) => ({key: t, label: t})));

  // Computed: Iterations & Score
  const normalizedIterations = computed(() => Math.max(1, Number(iterations.value) || 1));
  const normalizedMaxScore = computed(() => {
    const v = Number(maxScore.value);
    return Number.isFinite(v) && v > 0 ? v : null;
  });

  // Computed: Thinking Levels
  const suggestedThinkingLevels = computed(() => {
    const allLevels = new Set();
    const perModel = [];

    for (const sm of selectedModels.value) {
      const settings = getModelSettings(sm.provider, sm.model);
      if (settings.thinkingLevels.length > 0) {
        for (const level of settings.thinkingLevels) {
          allLevels.add(level);
        }
        perModel.push({provider: sm.provider, model: sm.model, levels: settings.thinkingLevels});
      }
    }

    return {all: [...allLevels], perModel};
  });

  // Computed: Run Matrix
  // selectedModels × parameterSets × iterations
  const runMatrix = computed(() => {
    return selectedModels.value
      .flatMap((selectedModel) => {
        return parameterSets.value.flatMap((parameterSet, parameterSetIndex) => {
          return Array.from({length: normalizedIterations.value}, (_, iterationIndex) => {
            return buildRunEntry(selectedModel, parameterSet, parameterSetIndex, iterationIndex + 1);
          });
        });
      })
      .map((entry, index) => ({...entry, index}));
  });

  // Computed: Status Counts
  const completedCount = computed(
    () => runs.value.filter((r) => ["completed", "failed", "timeout", "aborted"].includes(r.status)).length,
  );
  const totalCount = computed(() => runs.value.length);
  const passedCount = computed(() => runs.value.filter((r) => r.score === true).length);
  const failedScoreCount = computed(() => runs.value.filter((r) => r.score === false).length);
  const redFlagCount = computed(() => runs.value.filter((r) => r.redFlags === true).length);
  const yellowFlagCount = computed(() => runs.value.filter((r) => r.yellowFlags === true).length);

  const statusCompletedCount = computed(() => runs.value.filter((r) => r.status === "completed").length);
  const statusFailedCount = computed(() => runs.value.filter((r) => r.status === "failed").length);
  const statusTimeoutCount = computed(() => runs.value.filter((r) => r.status === "timeout").length);
  const statusAbortedCount = computed(() => runs.value.filter((r) => r.status === "aborted").length);
  const statusRunningCount = computed(() => runs.value.filter((r) => r.status === "running").length);

  // Computed: Progress
  const progressSegments = computed(() => {
    const total = totalCount.value;
    if (total === 0) return [];

    const segments = [
      {key: "completed", count: statusCompletedCount.value, color: "bg-green-500", label: "Completed"},
      {key: "failed", count: statusFailedCount.value, color: "bg-red-500", label: "Failed"},
      {key: "timeout", count: statusTimeoutCount.value, color: "bg-orange-400", label: "Timeout"},
      {key: "aborted", count: statusAbortedCount.value, color: "bg-gray-400", label: "Aborted"},
      {key: "running", count: statusRunningCount.value, color: "bg-blue-400 animate-pulse", label: "Running"},
    ];

    return segments.filter((s) => s.count > 0).map((s) => ({...s, percent: (s.count / total) * 100}));
  });

  // Computed: Can Start & Display
  const canStart = computed(() => {
    return !isRunning.value && selectedModels.value.length > 0 && parameterSets.value.length > 0 && prompt.value;
  });

  const durationDisplay = computed(() => {
    if (timerStart.value && timerEnd.value) {
      return ((timerEnd.value - timerStart.value) / 1000).toFixed(1) + "s";
    }
    if (timerStart.value && !timerEnd.value) {
      return elapsedSeconds.value + "s ...";
    }
    return "-";
  });

  const selectedRun = computed(() => {
    if (selectedRunIndex.value == null) return null;
    return runs.value[selectedRunIndex.value] ?? null;
  });

  // Computed: Summaries
  const configurationSummaries = computed(() => {
    const summaryMap = new Map();

    for (const run of runs.value) {
      const key = buildConfigurationSummaryKey(run);
      if (!summaryMap.has(key)) {
        summaryMap.set(key, createEmptySummary(run, key));
      }

      const summary = summaryMap.get(key);
      summary.totalRuns++;

      for (const warning of run.warnings ?? []) {
        summary.warnings.add(warning);
      }

      updateSummaryStatusCounts(summary, run.status);

      if (run.status === "completed") {
        updateSummaryWithCompletedRun(summary, run);
      }
    }

    const maxScoreValue = normalizedMaxScore.value;

    return [...summaryMap.values()]
      .map((summary) => finalizeSummary(summary, maxScoreValue))
      .toSorted((left, right) => {
        const leftScore = left.scorePercent ?? left.meanScore ?? Number.NEGATIVE_INFINITY;
        const rightScore = right.scorePercent ?? right.meanScore ?? Number.NEGATIVE_INFINITY;

        if (rightScore !== leftScore) {
          return rightScore - leftScore;
        }

        return left.meanDurationMs - right.meanDurationMs;
      });
  });

  const bestConfiguration = computed(() => {
    return configurationSummaries.value.find((summary) => summary.meanScore != null) ?? null;
  });

  const modelSummaries = computed(() => {
    const summaryMap = new Map();

    for (const run of runs.value) {
      const key = buildModelSummaryKey(run);
      if (!summaryMap.has(key)) {
        summaryMap.set(key, {
          key,
          provider: run.provider,
          model: run.model,
          totalRuns: 0,
          completedRuns: 0,
          failedRuns: 0,
          abortedRuns: 0,
          parameterSetLabels: new Set(),
        });
      }

      const summary = summaryMap.get(key);
      summary.totalRuns++;
      summary.parameterSetLabels.add(run.paramSetLabel);

      switch (run.status) {
        case "completed": {
          summary.completedRuns++;
          break;
        }
        case "failed":
        case "not_found": {
          summary.failedRuns++;
          break;
        }
        case "aborted": {
          summary.abortedRuns++;
          break;
        }
        default: {
          break;
        }
      }
    }

    return [...summaryMap.values()].map((summary) => ({
      ...summary,
      parameterSetLabels: [...summary.parameterSetLabels],
      isCompletelyBroken: summary.totalRuns > 0 && summary.completedRuns === 0,
    }));
  });

  const completelyBrokenModels = computed(() => {
    return modelSummaries.value.filter((summary) => {
      const hasAnyFinalState = summary.failedRuns > 0 || summary.abortedRuns > 0 || summary.completedRuns > 0;

      return summary.isCompletelyBroken && hasAnyFinalState;
    });
  });

  function buildPlaceholdersObject() {
    const map = {};
    for (const p of placeholders.value) {
      map[p.key] = p.value;
    }
    return map;
  }

  // Functions: Expression Evaluator
  // Intentional use of new Function() for user-defined JS expressions in this dev-only tool
  function createExpressionEvaluator(expressionReference, errorReference, defaultReturn) {
    function compile() {
      return new Function(
        "result",
        "placeholders",
        "try { " + expressionReference.value + " } catch (error) { return " + JSON.stringify(defaultReturn) + "; }",
      );
    }

    function evaluate(resultData) {
      if (!expressionReference.value.trim()) return defaultReturn;
      try {
        const raw = compile()(resultData, buildPlaceholdersObject());
        return defaultReturn === false ? Boolean(raw) : raw;
      } catch (error) {
        return defaultReturn === false ? false : "error: " + error.message;
      }
    }

    function test() {
      errorReference.value = null;
      try {
        compile();
      } catch (error) {
        errorReference.value = error.message;
      }
    }

    return {compile, evaluate, test};
  }

  // Functions: Parameter Sets
  function getDefaultParameterSet() {
    return {
      label: "",
      temperature: null,
      top_p: null,
      thinking_level: null,
      thinking_budget: null,
      max_steps: null,
      max_tokens: null,
    };
  }

  function addParameterSet() {
    parameterSets.value.push(getDefaultParameterSet());
  }

  function removeParameterSet(index) {
    parameterSets.value.splice(index, 1);
  }

  function getParameterSetLabel(parameterSet, index) {
    return parameterSet.label || "Set " + (index + 1);
  }

  // Functions: Model Selection
  function toggleProvider(provider) {
    collapsedProviders.value[provider] = !collapsedProviders.value[provider];
  }

  function isModelSelected(provider, model) {
    return selectedModels.value.some((m) => m.provider === provider && m.model === model);
  }

  function toggleModel(provider, model) {
    const index = selectedModels.value.findIndex((m) => m.provider === provider && m.model === model);
    if (index === -1) {
      selectedModels.value.push({provider, model});
    } else {
      selectedModels.value.splice(index, 1);
    }
  }

  function selectAllModels(provider) {
    const models = Object.keys(aiProviders[provider]?.models ?? {});
    for (const model of models) {
      if (!isModelSelected(provider, model)) {
        selectedModels.value.push({provider, model});
      }
    }
  }

  function deselectAllModels(provider) {
    selectedModels.value = selectedModels.value.filter((m) => m.provider !== provider);
  }

  function isAllSelected(provider) {
    const models = Object.keys(aiProviders[provider]?.models ?? {});
    return models.length > 0 && models.every((m) => isModelSelected(provider, m));
  }

  function getModelsWithUnusedThinking(parameterSet) {
    const thinkingValue = parameterSet.thinking_level?.trim() ?? "";

    if (!thinkingValue) {
      return suggestedThinkingLevels.value.perModel.map((pm) => ({
        provider: pm.provider,
        model: pm.model,
        levels: pm.levels,
        reason: "no thinking level set",
      }));
    }

    const candidates = thinkingValue
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    return suggestedThinkingLevels.value.perModel
      .filter((pm) => !candidates.some((candidate) => pm.levels.includes(candidate)))
      .map((pm) => ({
        provider: pm.provider,
        model: pm.model,
        levels: pm.levels,
        reason: "no match",
      }));
  }

  // Functions: Data Loading
  function loadInitialData() {
    if (!id.value || !feature.value) return;

    const params = {id: id.value, feature: feature.value, target: target.value};

    contextLoaded.value = false;
    systemPrompt.value = "";
    prompt.value = "";
    placeholders.value = [];
    technicalTermsJson.value = "{}";
    schemaJson.value = "{}";

    useAuthHttp()
      .get(route("ai-debugger.get-initial-data", params))
      .then((data) => {
        if (data.placeholders) {
          placeholders.value = Object.entries(data.placeholders).map(([key, value]) => {
            let isJson = true;
            try {
              value = JSON.stringify(JSON.parse(value), null, 2);
            } catch {
              isJson = false;
            }
            return {key, value, isJson, isCustom: false};
          });
        }

        if (data.technical_terms) {
          technicalTermsJson.value = JSON.stringify(data.technical_terms, null, 2);
        }
        if (data.schema) {
          schemaJson.value = JSON.stringify(data.schema, null, 2);
        }
        if (data.system_prompt) systemPrompt.value = data.system_prompt;
        if (data.prompt) prompt.value = data.prompt;
        if (data.replace_technical_terms) replaceTechnicalTerms.value = data.replace_technical_terms;
        if (data.enable_schema) enableSchema.value = data.enable_schema;

        contextLoaded.value = true;
      });
  }

  // Functions: Placeholders
  function addPlaceholder() {
    const name = globalThis.prompt("Placeholder Name (without %):");
    if (!name) return;
    placeholders.value.push({key: name.trim(), value: "", isJson: false, isCustom: true});
  }

  function insertPlaceholderIntoPrompt(key) {
    prompt.value = (prompt.value || "") + "%" + key + "%";
  }

  function removePlaceholder(index) {
    placeholders.value.splice(index, 1);
  }

  // Functions: Parsing
  function parseTechnicalTerms() {
    if (!replaceTechnicalTerms.value) return [];
    const parsed = JSON.parse(technicalTermsJson.value);
    return Object.entries(parsed).map(([term, replacement]) => ({term, replacement}));
  }

  function parseSchema() {
    if (!enableSchema.value) return null;
    return JSON.parse(schemaJson.value);
  }

  // Functions: Score & Flag Helpers
  function getNumericScoreValue(score) {
    if (score === true) return 1;
    if (score === false) return 0;
    if (typeof score === "number" && Number.isFinite(score)) return score;
    return null;
  }

  function computeRestoredScore(result, status) {
    if (status === "pending" || status === "running") return null;
    if (status === "completed") return evaluateScore(result);
    return false;
  }

  function scoreBadgeClass(score) {
    if (score === true) return "bg-green-100 text-green-700";
    if (score === false) return "bg-red-100 text-red-700";
    if (typeof score === "number") return "bg-blue-100 text-blue-700";
    if (typeof score === "string") return "bg-orange-100 text-orange-700";
    return "bg-gray-100 text-gray-500";
  }

  function scoreDisplay(score) {
    if (score === true) return "✓ PASS";
    if (score === false) return "✗ FAIL";
    if (typeof score === "number") return String(score);
    if (typeof score === "string") return score;
    return "–";
  }

  // Functions: Summary Helpers
  function buildConfigurationSummaryKey(run) {
    return [run.provider, run.model, run.paramSetIndex].join("::");
  }

  function buildModelSummaryKey(run) {
    return [run.provider, run.model].join("::");
  }

  function createEmptySummary(run, key) {
    return {
      key,
      provider: run.provider,
      model: run.model,
      paramSetIndex: run.paramSetIndex,
      paramSetLabel: run.paramSetLabel,
      totalRuns: 0,
      completedRuns: 0,
      failedRuns: 0,
      abortedRuns: 0,
      pendingRuns: 0,
      numericScoreSum: 0,
      numericScoreCount: 0,
      numericScoreMin: null,
      numericScoreMax: null,
      meanScore: null,
      durationMsSum: 0,
      durationMsCount: 0,
      redFlaggedRuns: 0,
      yellowFlaggedRuns: 0,
      warnings: new Set(),
    };
  }

  function updateSummaryStatusCounts(summary, status) {
    switch (status) {
      case "completed": {
        summary.completedRuns++;
        break;
      }
      case "failed":
      case "timeout":
      case "not_found": {
        summary.failedRuns++;
        break;
      }
      case "aborted": {
        summary.abortedRuns++;
        break;
      }
      default: {
        summary.pendingRuns++;
      }
    }
  }

  function updateSummaryWithCompletedRun(summary, run) {
    const numericScore = getNumericScoreValue(run.score);
    if (numericScore != null) {
      summary.numericScoreSum += numericScore;
      summary.numericScoreCount++;
      summary.numericScoreMin =
        summary.numericScoreMin == null ? numericScore : Math.min(summary.numericScoreMin, numericScore);
      summary.numericScoreMax =
        summary.numericScoreMax == null ? numericScore : Math.max(summary.numericScoreMax, numericScore);
    }

    if (run.durationMs != null) {
      summary.durationMsSum += run.durationMs;
      summary.durationMsCount++;
    }

    if (run.redFlags === true) summary.redFlaggedRuns++;
    if (run.yellowFlags === true) summary.yellowFlaggedRuns++;
  }

  function finalizeSummary(summary, maxScoreValue) {
    const meanScore = summary.numericScoreCount > 0 ? summary.numericScoreSum / summary.numericScoreCount : null;
    const scorePercent = meanScore != null && maxScoreValue != null ? (meanScore / maxScoreValue) * 100 : null;
    const meanDurationMs = summary.durationMsCount > 0 ? summary.durationMsSum / summary.durationMsCount : null;

    return {
      ...summary,
      meanScore,
      minScore: summary.numericScoreMin,
      maxScore: summary.numericScoreMax,
      scorePercent,
      meanDurationMs,
      redFlaggedRuns: summary.redFlaggedRuns,
      redFlagPercent: summary.completedRuns > 0 ? (summary.redFlaggedRuns / summary.completedRuns) * 100 : null,
      yellowFlaggedRuns: summary.yellowFlaggedRuns,
      yellowFlagPercent: summary.completedRuns > 0 ? (summary.yellowFlaggedRuns / summary.completedRuns) * 100 : null,
      warnings: [...summary.warnings],
      isCompletelyBroken: summary.completedRuns === 0 && summary.totalRuns > 0,
    };
  }

  // Functions: Run Building & Sanitization
  function resolveThinkingLevel(rawThinkingLevel, settings) {
    if (!rawThinkingLevel) return {resolved: null, warning: null};

    if (!settings.supportsThinking) {
      return {resolved: null, warning: "thinking_level not supported → skipped"};
    }

    const candidates = rawThinkingLevel
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    for (const candidate of candidates) {
      if (settings.thinkingLevels.includes(candidate)) {
        if (candidates.length > 1) {
          return {resolved: candidate, warning: `thinking_level resolved to "${candidate}" (first available match)`};
        }
        return {resolved: candidate, warning: null};
      }
    }

    return {
      resolved: null,
      warning: `thinking_level: none of [${candidates.join(", ")}] available (model supports: ${settings.thinkingLevels.join(", ") || "none"}) → skipped`,
    };
  }

  function sanitizeParameterSet(selectedModel, parameterSet) {
    const settings = getModelSettings(selectedModel.provider, selectedModel.model);
    const warnings = [];

    const {resolved: thinkingLevel, warning: thinkingWarning} = resolveThinkingLevel(
      parameterSet.thinking_level,
      settings,
    );
    if (thinkingWarning) warnings.push(thinkingWarning);

    let thinkingBudget = parameterSet.thinking_budget;
    let topP = parameterSet.top_p;
    let temperature = parameterSet.temperature;

    if (thinkingBudget && !settings.supportsThinkingBudget) {
      warnings.push("thinking_budget not supported → skipped");
      thinkingBudget = null;
    }

    if (topP != null && !settings.supportsTopP) {
      warnings.push("top_p not supported → skipped");
      topP = null;
    }

    if (temperature != null) {
      if (!settings.supportsTemperature) {
        warnings.push("temperature not supported → skipped");
        temperature = null;
      } else if (selectedModel.provider === "anthropic" && thinkingLevel === "ON") {
        warnings.push("temperature + thinking not supported → skipped temperature");
        temperature = null;
      }
    }

    return {
      temperature,
      top_p: topP,
      thinking_level: thinkingLevel,
      thinking_budget: thinkingBudget,
      include_thoughts: settings.supportsThinking,
      max_steps: parameterSet.max_steps,
      max_tokens: parameterSet.max_tokens,
      warnings,
    };
  }

  function buildRunEntry(selectedModel, parameterSet, parameterSetIndex, iteration) {
    return {
      provider: selectedModel.provider,
      model: selectedModel.model,
      paramSetIndex: parameterSetIndex,
      paramSetLabel: getParameterSetLabel(parameterSet, parameterSetIndex),
      iteration,
      ...sanitizeParameterSet(selectedModel, parameterSet),
    };
  }

  function buildCombinations() {
    return runMatrix.value.map((entry) => ({
      provider: entry.provider,
      model: entry.model,
      temperature: entry.temperature,
      top_p: entry.top_p,
      include_thoughts: entry.include_thoughts,
      thinking_level: entry.thinking_level,
      thinking_budget: entry.thinking_budget,
      max_steps: entry.max_steps,
      max_tokens: entry.max_tokens,
      iteration: entry.iteration,
      param_set_index: entry.paramSetIndex,
    }));
  }

  function initializeRuns() {
    runs.value = runMatrix.value.map((entry) => ({
      ...entry,
      runId: null,
      source: null,
      status: "queuing",
      result: null,
      error: null,
      score: null,
      redFlags: false,
      yellowFlags: false,
      startedAt: null,
      completedAt: null,
      durationMs: null,
    }));
  }

  // Functions: Run Status
  function getPendingRuns() {
    return runs.value.filter((run) => run.runId && ["pending", "running"].includes(run.status));
  }

  function applyRunCompletion(run, data) {
    run.completedAt = Date.now();
    run.durationMs = data.duration_ms ?? (run.startedAt ? run.completedAt - run.startedAt : null);

    if (data.status === "completed") {
      run.result = data.result;
      run.error = null;
      run.score = evaluateScore(data.result);
      run.redFlags = evaluateRedFlag(data.result);
      run.yellowFlags = evaluateYellowFlag(data.result);
      return;
    }

    run.error = data.error || (data.status === "timeout" ? "Job timed out." : "Job failed.");
    run.score = false;
    run.redFlags = false;
    run.yellowFlags = false;
  }

  function applyCachedJob(run, job) {
    run.source = "cache";
    run.startedAt = Date.now();
    run.status = job.status;
    applyRunCompletion(run, job);
    // For cached jobs, use the original duration if available, otherwise 0
    if (job.duration_ms == null) {
      run.durationMs = 0;
    }
  }

  function applyStatuses(statuses) {
    for (const run of runs.value) {
      if (!run.runId || !statuses[run.runId]) {
        continue;
      }

      const data = statuses[run.runId];
      run.status = data.status;

      if (["completed", "failed", "timeout", "not_found", "aborted"].includes(data.status)) {
        applyRunCompletion(run, data.status === "not_found" ? {...data, status: "failed"} : data);
      }
    }
  }

  // Functions: Mass Run
  function startMassRun() {
    globalError.value = null;
    selectedRunIndex.value = null;

    if (replaceTechnicalTerms.value) {
      try {
        parseTechnicalTerms();
      } catch {
        globalError.value = "Technical Terms JSON is invalid.";
        return;
      }
    }

    if (enableSchema.value) {
      try {
        parseSchema();
      } catch {
        globalError.value = "Schema JSON is invalid.";
        return;
      }
    }

    const combinations = buildCombinations();

    if (combinations.length === 0) {
      globalError.value = "No combinations to run. Select models and parameter sets.";
      return;
    }

    initializeRuns();

    isRunning.value = true;
    timerStart.value = Date.now();
    timerEnd.value = null;
    elapsedSeconds.value = 0;
    startElapsedTimer();

    const payload = {
      context_id: id.value,
      feature: feature.value,
      target: target.value || null,
      disable_cache: disableCache.value,
      disable_failed_cache: disableFailedCache.value,
      system_prompt: systemPrompt.value || null,
      prompt: prompt.value,
      replace_technical_terms: replaceTechnicalTerms.value,
      enable_schema: enableSchema.value,
      schema: parseSchema(),
      placeholders: placeholders.value,
      technical_terms: parseTechnicalTerms(),
      parameter_sets: parameterSets.value,
      iterations: normalizedIterations.value,
      combinations,
      evaluation: {
        score_expression: scoreExpression.value,
        max_score: maxScore.value,
        red_flag_expression: redFlagExpression.value,
        yellow_flag_expression: yellowFlagExpression.value,
      },
    };

    useAuthHttp(payload)
      .post(route("ai-mass-debugger.mass-queue-start"))
      .then((response) => {
        const jobs = response.jobs;
        let hasQueuedJobs = false;

        for (const job of jobs) {
          const run = runs.value[job.combinationIndex];
          if (!run) {
            continue;
          }

          if (job.source === "cache") {
            applyCachedJob(run, job);
            continue;
          }

          run.source = "queue";
          run.runId = job.runId;
          run.status = job.status ?? "pending";
          run.startedAt = Date.now();
          hasQueuedJobs = true;
        }

        // Refresh history so the running batch appears in the sidebar
        fetchHistory();

        if (hasQueuedJobs) {
          startPolling();
          return;
        }

        finishMassRun();
      })
      .catch((error) => {
        isRunning.value = false;
        timerEnd.value = Date.now();
        stopElapsedTimer();
        globalError.value = "Mass queue start failed: " + JSON.stringify(error.message ?? error);
      });
  }

  function finishMassRun() {
    if (!isRunning.value) {
      return;
    }

    stopPolling();
    stopElapsedTimer();
    isRunning.value = false;
    timerEnd.value = Date.now();
    addToHistory();
  }

  // Functions: Polling
  function startPolling() {
    stopPolling();
    pollInterval = setInterval(pollStatus, 2000);
  }

  function stopPolling() {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  }

  function pollStatus() {
    const pendingRuns = getPendingRuns();
    if (pendingRuns.length === 0) {
      finishMassRun();
      return;
    }

    const runIds = pendingRuns.map((run) => run.runId);

    useAuthHttp({runIds})
      .post(route("ai-mass-debugger.mass-queue-status"))
      .then((response) => {
        applyStatuses(response);

        if (getPendingRuns().length === 0) {
          finishMassRun();
        }
      })
      .catch(() => {
        // silently retry on network issues
      });
  }

  // Functions: Timer
  function startElapsedTimer() {
    stopElapsedTimer();
    elapsedInterval = setInterval(() => {
      elapsedSeconds.value = ((Date.now() - timerStart.value) / 1000).toFixed(1);
    }, 100);
  }

  function stopElapsedTimer() {
    if (elapsedInterval) {
      clearInterval(elapsedInterval);
      elapsedInterval = null;
    }
  }

  // Functions: Abort
  function abortAll() {
    const runIdsToAbort = runs.value
      .filter((run) => run.runId && ["pending", "running"].includes(run.status))
      .map((run) => run.runId);

    // Mark runs that never got a runId (still "queuing") as aborted immediately
    for (const run of runs.value) {
      if (!run.runId && run.status === "queuing") {
        run.status = "aborted";
      }
    }

    if (runIdsToAbort.length === 0) {
      finishMassRun();
      return;
    }

    useAuthHttp({runIds: runIdsToAbort})
      .post(route("ai-mass-debugger.mass-queue-abort"))
      .then(() => {
        // Trigger an immediate poll so the UI picks up the aborted statuses quickly
        pollStatus();
      })
      .catch(() => {
        // If the backend request failed, force-finish to avoid being stuck
        finishMassRun();
      });
  }

  function abortAllPendingGlobally() {
    if (
      !globalThis.confirm(
        "Abort ALL pending/running AI debug jobs across all batches? This will also cancel the queue.",
      )
    ) {
      return;
    }

    isAbortingAll.value = true;
    abortAllResult.value = null;

    useAuthHttp()
      .post(route("ai-mass-debugger.abort-all-pending"))
      .then((response) => {
        abortAllResult.value = response;
        fetchHistory();

        // If we have a running batch, trigger a poll to pick up aborted statuses
        if (isRunning.value) {
          pollStatus();
        }
      })
      .catch((error) => {
        globalError.value = "Abort all failed: " + error.message;
      })
      .finally(() => {
        isAbortingAll.value = false;
      });
  }

  // Functions: History
  function fetchHistory() {
    historyLoading.value = true;
    useAuthHttp()
      .get(route("ai-mass-debugger.history"))
      .then((response) => {
        history.value = response;
      })
      .catch((error) => {
        console.warn("Failed to load mass debugger history:", error);
      })
      .finally(() => {
        historyLoading.value = false;
      });
  }

  function addToHistory() {
    fetchHistory();
  }

  function restoreFromHistory(entry) {
    stopPolling();
    stopElapsedTimer();
    isRunning.value = false;

    historyLoading.value = true;
    useAuthHttp()
      .get(route("ai-mass-debugger.history-detail", {batchId: entry.batchId}))
      .then((data) => {
        if (data.runs.length === 0) return;

        const batchConfig = data.config ?? {};
        const evaluation = batchConfig.evaluation ?? {};

        id.value = batchConfig.context_id ?? "";
        feature.value = batchConfig.feature ?? "";
        target.value = batchConfig.target ?? "";

        systemPrompt.value = batchConfig.system_prompt ?? "";
        prompt.value = batchConfig.prompt ?? "";

        scoreExpression.value =
          evaluation.score_expression ?? "return result.response && result.response.length > 0 ? 10 : 0;";
        maxScore.value = evaluation.max_score ?? 10;
        redFlagExpression.value = evaluation.red_flag_expression ?? "return false;";
        yellowFlagExpression.value = evaluation.yellow_flag_expression ?? "return false;";

        replaceTechnicalTerms.value = batchConfig.replace_technical_terms ?? false;
        enableSchema.value = batchConfig.enable_schema ?? false;

        if (Array.isArray(batchConfig.placeholders) && batchConfig.placeholders.length > 0) {
          placeholders.value = batchConfig.placeholders.map((p) => ({...p, isCustom: true}));
        } else {
          placeholders.value = [];
        }

        if (Array.isArray(batchConfig.technical_terms) && batchConfig.technical_terms.length > 0) {
          const termsObject = {};
          for (const t of batchConfig.technical_terms) {
            termsObject[t.term] = t.replacement;
          }
          technicalTermsJson.value = JSON.stringify(termsObject, null, 2);
        } else {
          technicalTermsJson.value = "{}";
        }

        schemaJson.value = batchConfig.schema ? JSON.stringify(batchConfig.schema, null, 2) : "{}";
        contextLoaded.value = true;

        selectedModels.value = batchConfig.selected_models ?? [];

        parameterSets.value =
          Array.isArray(batchConfig.parameter_sets) && batchConfig.parameter_sets.length > 0
            ? batchConfig.parameter_sets
            : [getDefaultParameterSet()];

        iterations.value = batchConfig.iterations ?? 1;

        selectedRunIndex.value = null;
        runs.value = data.runs.map((serverRun, index) => {
          const config = serverRun.config ?? {};
          const result = serverRun.result ?? null;
          const paramSetIndex = config.param_set_index ?? 0;

          return {
            index,
            provider: serverRun.provider,
            model: serverRun.model,
            paramSetIndex,
            paramSetLabel: getParameterSetLabel(parameterSets.value[paramSetIndex], paramSetIndex),
            iteration: config.iteration ?? 1,
            temperature: config.temperature ?? null,
            top_p: config.top_p ?? null,
            thinking_level: config.thinking_level ?? null,
            thinking_budget: config.thinking_budget ?? null,
            include_thoughts: config.include_thoughts ?? false,
            max_steps: config.max_steps ?? null,
            max_tokens: config.max_tokens ?? null,
            warnings: [],
            runId: serverRun.runId,
            source: "history",
            status: serverRun.status,
            result,
            error: serverRun.error,
            score: computeRestoredScore(result, serverRun.status),
            redFlags: result ? evaluateRedFlag(result) : false,
            yellowFlags: result ? evaluateYellowFlag(result) : false,
            startedAt: serverRun.timestamp,
            completedAt: serverRun.timestamp + (serverRun.durationMs ?? 0),
            durationMs: serverRun.durationMs,
          };
        });

        const hasPendingRuns = runs.value.some((run) => run.runId && ["pending", "running"].includes(run.status));

        if (hasPendingRuns) {
          isRunning.value = true;
          timerStart.value = entry.timestamp;
          timerEnd.value = null;
          elapsedSeconds.value = ((Date.now() - entry.timestamp) / 1000).toFixed(1);
          startElapsedTimer();
          startPolling();
        } else {
          isRunning.value = false;
          timerStart.value = entry.timestamp;
          timerEnd.value = entry.timestamp + (entry.maxDurationMs ?? 0);
        }
      })
      .catch((error) => {
        console.warn("Failed to load batch detail:", error);
      })
      .finally(() => {
        historyLoading.value = false;
      });
  }

  function isHistoryEntryRunning(entry) {
    const finishedRuns = entry.completedRuns + entry.failedRuns + entry.abortedRuns;
    return finishedRuns < entry.totalRuns;
  }

  function historyEntryStatus(entry) {
    if (isHistoryEntryRunning(entry)) return "running";
    if (entry.failedRuns > 0 && entry.completedRuns === 0) return "failed";
    return "completed";
  }

  // Functions: Formatting
  function formatHistoryTimestamp(ts) {
    return new Date(ts).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  function formatScore(score) {
    if (score == null) return "-";
    return score.toFixed(2);
  }

  function formatScorePercent(percent) {
    if (percent == null) return "-";
    return Math.round(percent) + "%";
  }

  function formatDuration(ms) {
    if (!ms) return "-";
    return (ms / 1000).toFixed(1) + "s";
  }

  // Functions: TSV Export
  function escapeTsvCell(value) {
    if (value == null) return "";
    let string_ = String(value);
    // Replace actual newlines with literal \n so the cell stays on one line
    string_ = string_
      .replaceAll("\r\n", String.raw`\n`)
      .replaceAll("\r", String.raw`\n`)
      .replaceAll("\n", String.raw`\n`);
    // If the value contains tabs or double-quotes, wrap in quotes (CSV/TSV convention)
    if (string_.includes("\t") || string_.includes('"')) {
      string_ = '"' + string_.replaceAll('"', '""') + '"';
    }
    return string_;
  }

  function buildTsvLine(cells) {
    return cells.map((element) => escapeTsvCell(element)).join("\t");
  }

  function exportContextSection(lines) {
    const totalDurationMs = timerEnd.value && timerStart.value ? timerEnd.value - timerStart.value : null;

    lines.push(
      "## Context",
      buildTsvLine(["Timestamp", new Date().toLocaleString("de-DE")]),
      buildTsvLine(["Feature", feature.value]),
      buildTsvLine(["Target", target.value]),
      buildTsvLine(["ID", id.value]),
      buildTsvLine(["Iterations", iterations.value]),
      buildTsvLine(["Disable Cache", disableCache.value ? "Yes" : "No"]),
      buildTsvLine(["Disable Failed Cache", disableFailedCache.value ? "Yes" : "No"]),
      buildTsvLine(["Replace Technical Terms", replaceTechnicalTerms.value ? "Yes" : "No"]),
      buildTsvLine(["Structured Output (Schema)", enableSchema.value ? "Yes" : "No"]),
      buildTsvLine(["Total Duration", totalDurationMs == null ? "-" : (totalDurationMs / 1000).toFixed(1) + "s"]),
      "",
      "## Selected Models",
    );
    for (const sm of selectedModels.value) {
      lines.push(buildTsvLine([sm.provider, sm.model]));
    }
    lines.push(
      "",
      "## Prompts",
      buildTsvLine(["System Prompt", systemPrompt.value]),
      buildTsvLine(["Prompt", prompt.value]),
      "",
    );
  }

  function exportPlaceholdersSection(lines) {
    if (placeholders.value.length === 0) return;
    lines.push("## Placeholders", buildTsvLine(["Key", "Value"]));
    for (const p of placeholders.value) {
      lines.push(buildTsvLine(["%" + p.key + "%", p.value]));
    }
    lines.push("");
  }

  function exportTechnicalTermsSection(lines) {
    if (!replaceTechnicalTerms.value) return;
    lines.push("## Technical Terms", buildTsvLine(["Term", "Replacement"]));
    try {
      const terms = JSON.parse(technicalTermsJson.value);
      for (const [term, replacement] of Object.entries(terms)) {
        lines.push(buildTsvLine([term, replacement]));
      }
    } catch {
      lines.push(buildTsvLine(["(invalid JSON)", technicalTermsJson.value]));
    }
    lines.push("");
  }

  function exportSchemaSection(lines) {
    if (!enableSchema.value) return;
    lines.push("## Schema", buildTsvLine([schemaJson.value]), "");
  }

  function exportEvaluationSection(lines) {
    lines.push(
      "## Evaluation",
      buildTsvLine(["Score Expression", scoreExpression.value]),
      buildTsvLine(["Max Score", maxScore.value]),
      buildTsvLine(["Red Flag Expression", redFlagExpression.value]),
      buildTsvLine(["Yellow Flag Expression", yellowFlagExpression.value]),
      "",
      "## Parameter Sets",
      buildTsvLine([
        "#",
        "Label",
        "Temperature",
        "Top P",
        "Thinking Level",
        "Thinking Budget",
        "Max Steps",
        "Max Tokens",
      ]),
    );

    for (const [psIndex, ps] of parameterSets.value.entries()) {
      lines.push(
        buildTsvLine([
          psIndex + 1,
          getParameterSetLabel(ps, psIndex),
          ps.temperature ?? "",
          ps.top_p ?? "",
          ps.thinking_level ?? "",
          ps.thinking_budget ?? "",
          ps.max_steps ?? "",
          ps.max_tokens ?? "",
        ]),
      );
    }
    lines.push("");
  }

  function exportSummarySection(lines) {
    const hasMaxScore = normalizedMaxScore.value != null;
    const summaryHeaders = ["Provider", "Model", "Parameter Set", "Mean Score", "Min Score", "Max Score"];
    if (hasMaxScore) summaryHeaders.push("Score %");
    summaryHeaders.push(
      "Mean Duration",
      "Red Flagged %",
      "Red Flagged",
      "Yellow Flagged %",
      "Yellow Flagged",
      "Completed",
      "Failed",
      "Warnings",
    );

    lines.push("## Summary", buildTsvLine(summaryHeaders));

    for (const s of configurationSummaries.value) {
      const row = [
        s.provider,
        s.model,
        s.paramSetLabel,
        formatScore(s.meanScore),
        formatScore(s.minScore),
        formatScore(s.maxScore),
      ];
      if (hasMaxScore) row.push(formatScorePercent(s.scorePercent));
      row.push(
        formatDuration(s.meanDurationMs),
        s.redFlagPercent == null ? "-" : Math.round(s.redFlagPercent) + "%",
        s.redFlaggedRuns + "/" + s.completedRuns,
        s.yellowFlagPercent == null ? "-" : Math.round(s.yellowFlagPercent) + "%",
        s.yellowFlaggedRuns + "/" + s.completedRuns,
        `${s.completedRuns}/${s.totalRuns}`,
        s.failedRuns,
        (s.warnings ?? []).join("; "),
      );
      lines.push(buildTsvLine(row));
    }
  }

  function exportRunsSection(lines) {
    lines.push(
      "",
      "## Runs",
      buildTsvLine([
        "#",
        "Provider",
        "Model",
        "Param Set",
        "Iteration",
        "Status",
        "Duration (s)",
        "Score",
        "Red Flag",
        "Yellow Flag",
        "Error",
        "Warnings",
        "Response",
      ]),
    );

    for (const [rIndex, r] of runs.value.entries()) {
      const durationSec = r.durationMs ? (r.durationMs / 1000).toFixed(1) : "";
      const score = r.score == null ? "" : scoreDisplay(r.score);
      const warnings = (r.warnings ?? []).join("; ");
      const response = r.result?.response ?? "";

      lines.push(
        buildTsvLine([
          rIndex + 1,
          r.provider,
          r.model,
          r.paramSetLabel,
          r.iteration,
          r.status,
          durationSec,
          score,
          r.redFlags === true ? "⚑" : "",
          r.yellowFlags === true ? "⚐" : "",
          r.error ?? "",
          warnings,
          response,
        ]),
      );
    }
  }

  function exportResults() {
    const lines = [];

    exportContextSection(lines);
    exportPlaceholdersSection(lines);
    exportTechnicalTermsSection(lines);
    exportSchemaSection(lines);
    exportEvaluationSection(lines);
    exportSummarySection(lines);
    exportRunsSection(lines);

    const tsv = lines.join("\n");
    const blob = new Blob([tsv], {type: "text/tab-separated-values"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai-mass-debug-${Date.now()}.tsv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Lifecycle Hooks
  onBeforeUnmount(() => {
    stopPolling();
    stopElapsedTimer();
  });

  // Initial Calls
  loadInitialData();
  fetchHistory();
</script>

<template>
  <div class="space-y-6">
    <div class="flex items-center gap-4">
      <h1 class="text-2xl font-bold">AI Mass Debugger</h1>
      <a :href="$route('ai-debugger.index')" class="text-sm text-blue-600 hover:underline">← Single Debugger</a>
      <div class="ml-auto flex items-center gap-2">
        <span v-if="abortAllResult" class="text-xs text-gray-500">
          Aborted {{ abortAllResult.abortedRuns }} run(s), cancelled {{ abortAllResult.cancelledBatches }} batch(es)
        </span>
        <MedButton variant="destructive" size="sm" :is-loading="isAbortingAll" @click="abortAllPendingGlobally">
          <ComponentIcon class="mr-1 text-sm">cancel</ComponentIcon>
          Abort All Jobs
        </MedButton>
      </div>
    </div>

    <div class="flex items-start gap-6">
      <!-- History Sidebar -->
      <MedCard
        as="aside"
        class="sticky top-20 flex w-72 shrink-0 flex-col overflow-hidden"
        :style="{maxHeight: 'calc(100vh - 2rem)'}"
      >
        <MedCardHeader class="mb-2 flex items-center justify-between">
          <span>
            History
            <span class="ml-1 text-xs font-normal">({{ history.length }})</span>
          </span>
          <MedButton variant="secondary" size="sm" :disabled="historyLoading" @click="fetchHistory">
            <ComponentIcon class="text-sm">refresh</ComponentIcon>
          </MedButton>
        </MedCardHeader>

        <MedCardContent v-if="historyLoading && history.length === 0" class="py-12 text-center text-sm">
          Loading history...
        </MedCardContent>

        <MedCardContent v-else-if="history.length === 0" class="py-12 text-center text-sm">
          No mass runs yet.
          <br />
          Results will appear here automatically.
        </MedCardContent>

        <MedCardContent v-else class="flex-1 divide-y divide-gray-100 overflow-y-auto p-0!">
          <MedButton
            v-for="entry in history"
            :key="entry.batchId"
            variant="ghost"
            full-width
            class="group h-auto! items-start text-left"
            @click="restoreFromHistory(entry)"
          >
            <span class="min-w-0 flex-1">
              <span class="flex items-center gap-1.5">
                <AiStatusBadge :status="historyEntryStatus(entry)" icon-only class="shrink-0" />
                <span class="truncate text-xs font-medium">
                  {{ entry.feature }}
                  <template v-if="entry.target">/{{ entry.target }}</template>
                </span>
              </span>
              <span class="mt-0.5 block truncate text-[11px] text-gray-500">
                {{ formatHistoryTimestamp(entry.timestamp) }}
              </span>
            </span>
          </MedButton>
        </MedCardContent>
      </MedCard>

      <!-- Main Content -->
      <div class="min-w-0 flex-1 space-y-6">
        <!-- Context Card -->
        <MedCard>
          <MedCardHeader>Context</MedCardHeader>
          <MedCardContent>
            <div class="flex gap-4">
              <ComponentInput v-model="id" label="ID" :hide-clear="true" />
              <ComponentSelect
                v-model="feature"
                label="Feature"
                :options="featureOptions"
                key-name="label"
                key-value="key"
                :nullable="true"
                placeholder-text="Select feature..."
              />
              <ComponentSelect
                v-model="target"
                label="Target (optional)"
                :options="targetOptions"
                key-name="label"
                key-value="key"
                :nullable="true"
                placeholder-text="Select target..."
              />
              <div class="flex items-end">
                <MedButton variant="secondary" :disabled="!id || !feature" @click="loadInitialData">Load</MedButton>
              </div>
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Prompt Configuration -->
        <MedCard>
          <MedCardHeader>Prompt Configuration</MedCardHeader>
          <MedCardContent class="space-y-4">
            <ComponentTextarea v-model="systemPrompt" label="System-Prompt" />
            <ComponentTextarea v-model="prompt" label="Prompt" />

            <div v-if="placeholders.length > 0" class="flex flex-wrap gap-2">
              <MedButton
                v-for="placeholder in placeholders"
                :key="placeholder.key"
                variant="outline"
                size="sm"
                @click="insertPlaceholderIntoPrompt(placeholder.key)"
              >
                <ComponentIcon class="mr-1 text-sm">add</ComponentIcon>
                %{{ placeholder.key }}%
              </MedButton>
            </div>

            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold">Placeholders</h3>
                <MedButton variant="secondary" size="sm" @click="addPlaceholder">
                  <ComponentIcon class="mr-1 text-sm">add</ComponentIcon>
                  Add Placeholder
                </MedButton>
              </div>
              <template v-for="(placeholder, index) in placeholders" :key="index">
                <div class="flex gap-2">
                  <div class="flex-1">
                    <ComponentTextarea v-model="placeholder.value" :label="'%' + placeholder.key + '%'" />
                  </div>
                  <div v-if="placeholder.isCustom" class="flex items-end pb-1">
                    <MedButton variant="destructive" size="sm" @click="removePlaceholder(index)">
                      <ComponentIcon class="text-sm">delete</ComponentIcon>
                    </MedButton>
                  </div>
                </div>
              </template>
            </div>

            <div class="space-y-3">
              <MedFormField orientation="horizontal">
                <MedCheckbox v-model="replaceTechnicalTerms" variant="default" />
                <MedLabel clickable>Replace technical with layman terms</MedLabel>
              </MedFormField>
              <ComponentTextarea
                v-if="replaceTechnicalTerms"
                v-model="technicalTermsJson"
                label="Technical Terms (key = technical term → value = replacement)"
              />
            </div>

            <div class="space-y-3">
              <MedFormField orientation="horizontal">
                <MedCheckbox v-model="enableSchema" variant="default" />
                <MedLabel clickable>Structured Output (Schema)</MedLabel>
              </MedFormField>
              <ComponentTextarea v-if="enableSchema" v-model="schemaJson" label="JSON Schema" />
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Model Selection -->
        <MedCard>
          <MedCardHeader>
            Models
            <span class="ml-2 text-sm font-normal text-gray-500">({{ selectedModels.length }} selected)</span>
          </MedCardHeader>
          <MedCardContent class="space-y-3">
            <div v-for="(providerConfig, providerName) in aiProviders" :key="providerName">
              <div class="flex items-center gap-2 rounded bg-gray-50 px-3 py-2">
                <MedButton variant="ghost" size="sm" @click="toggleProvider(providerName)">
                  <ComponentIcon class="text-sm">
                    {{ collapsedProviders[providerName] ? "chevron_right" : "expand_more" }}
                  </ComponentIcon>
                </MedButton>
                <span class="text-sm font-semibold">{{ providerName }}</span>
                <span class="text-xs text-gray-400">({{ Object.keys(providerConfig.models).length }} models)</span>
                <div class="ml-auto flex gap-1">
                  <MedButton
                    variant="outline"
                    size="sm"
                    :disabled="isAllSelected(providerName)"
                    @click="selectAllModels(providerName)"
                  >
                    Select All
                  </MedButton>
                  <MedButton variant="outline" size="sm" @click="deselectAllModels(providerName)">Deselect</MedButton>
                </div>
              </div>

              <div v-if="!collapsedProviders[providerName]" class="mt-1 ml-10 flex flex-wrap gap-x-4 gap-y-1.5 pb-2">
                <MedFormField
                  v-for="modelName in Object.keys(providerConfig.models)"
                  :key="modelName"
                  orientation="horizontal"
                  class="text-sm"
                >
                  <MedCheckbox
                    :model-value="isModelSelected(providerName, modelName)"
                    variant="default"
                    @update:model-value="toggleModel(providerName, modelName)"
                  />
                  <MedLabel clickable class="font-mono text-xs">{{ modelName }}</MedLabel>
                </MedFormField>
              </div>
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Parameter Sets -->
        <MedCard>
          <MedCardHeader class="flex items-center justify-between">
            <span>Parameter Sets</span>
            <MedButton variant="secondary" size="sm" @click="addParameterSet">
              <ComponentIcon class="mr-1 text-sm">add</ComponentIcon>
              Add Parameter Set
            </MedButton>
          </MedCardHeader>
          <MedCardContent class="space-y-4">
            <div
              v-for="(parameterSet, parameterSetIndex) in parameterSets"
              :key="parameterSetIndex"
              class="rounded-lg border border-gray-200 p-4"
            >
              <div class="mb-3 flex items-center justify-between">
                <h4 class="text-sm font-semibold">
                  {{ getParameterSetLabel(parameterSet, parameterSetIndex) }}
                </h4>
                <MedButton
                  v-if="parameterSets.length > 1"
                  variant="destructive"
                  size="sm"
                  @click="removeParameterSet(parameterSetIndex)"
                >
                  <ComponentIcon class="text-sm">delete</ComponentIcon>
                </MedButton>
              </div>

              <div class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                <ComponentInput v-model="parameterSet.label" label="Label (optional)" />
                <ComponentInput v-model="parameterSet.temperature" type="number" label="Temperature" />
                <ComponentInput v-model="parameterSet.top_p" type="number" label="Top P" />
                <div>
                  <ComponentInput v-model="parameterSet.thinking_level" label="Thinking Level (comma-sep)" />
                  <p v-if="suggestedThinkingLevels.all.length > 0" class="mt-1 text-[11px] leading-tight text-gray-500">
                    Possible:
                    <button
                      v-for="(level, levelIndex) in suggestedThinkingLevels.all"
                      :key="level"
                      type="button"
                      class="cursor-pointer rounded bg-gray-100 px-1 py-0.5 font-mono text-[10px] hover:bg-blue-100"
                      @click="
                        parameterSet.thinking_level = parameterSet.thinking_level
                          ? parameterSet.thinking_level + ', ' + level
                          : level
                      "
                    >
                      {{ levelIndex > 0 ? " " : "" }}{{ level }}
                    </button>
                  </p>
                  <details v-if="suggestedThinkingLevels.perModel.length > 0" class="mt-1 text-[10px] text-gray-400">
                    <summary class="cursor-pointer hover:text-gray-600">Per-model details</summary>
                    <ul class="mt-0.5 space-y-0.5 pl-2">
                      <li v-for="pm in suggestedThinkingLevels.perModel" :key="pm.provider + '/' + pm.model">
                        <span class="font-mono">{{ pm.provider }}/{{ pm.model }}</span>
                        :
                        {{ pm.levels.join(", ") }}
                      </li>
                    </ul>
                  </details>
                  <div
                    v-if="getModelsWithUnusedThinking(parameterSet).length > 0"
                    class="mt-1 text-[11px] leading-tight text-amber-600"
                  >
                    ⚠ Thinking level mismatch:
                    <ul class="mt-0.5 list-disc pl-4">
                      <li v-for="pm in getModelsWithUnusedThinking(parameterSet)" :key="pm.provider + '/' + pm.model">
                        <span class="font-mono">{{ pm.provider }}/{{ pm.model }}</span>
                        - {{ pm.reason }} (supports: {{ pm.levels.join(", ") }})
                      </li>
                    </ul>
                  </div>
                </div>
                <ComponentInput v-model="parameterSet.thinking_budget" type="number" label="Thinking Budget" />
                <ComponentInput v-model="parameterSet.max_steps" type="number" label="Max Steps" />
                <ComponentInput v-model="parameterSet.max_tokens" type="number" label="Max Tokens" />
              </div>
            </div>
          </MedCardContent>
        </MedCard>

        <MedCard>
          <MedCardHeader>Iterations &amp; Score Calculator</MedCardHeader>
          <MedCardContent class="space-y-4">
            <div class="flex gap-4">
              <ComponentInput
                v-model="iterations"
                type="number"
                label="Iterations per combination"
                :hide-clear="true"
              />
              <div class="flex-1" />
            </div>

            <div class="space-y-3 rounded-lg border border-gray-200 p-4">
              <h3 class="text-sm font-semibold">Cache</h3>

              <div class="flex flex-col gap-3">
                <div class="flex items-center gap-3">
                  <ComponentSwitch v-model="disableCache" color="primary" />
                  <div>
                    <div class="text-sm font-medium">Disable cache</div>
                    <div class="text-xs text-gray-500">Always re-queue, never reuse server-side cached results.</div>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <ComponentSwitch v-model="disableFailedCache" color="primary" :disabled="disableCache" />
                  <div>
                    <div class="text-sm font-medium">Disable failed cache</div>
                    <div class="text-xs text-gray-500">
                      Ignore failed cached runs, only reuse successful cache hits.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <div class="flex gap-4">
                <div class="flex-1">
                  <ComponentTextarea
                    v-model="scoreExpression"
                    label="Score Expression (JS)"
                    @blur="testScoreExpression"
                  />
                </div>
                <div class="w-40">
                  <ComponentInput v-model="maxScore" type="number" label="Max Score" :hide-clear="true" />
                  <p class="mt-1 text-[11px] text-gray-400">For % calculation</p>
                </div>
              </div>
              <p class="text-xs text-gray-500">
                Available:
                <code>result</code>
                object with
                <code>.response</code>
                ,
                <code>.usage</code>
                ,
                <code>.steps</code>
                ,
                <code>.thoughts</code>
                , etc.
                <br />
                Also:
                <code>placeholders</code>
                object keyed by placeholder name (e.g.
                <code>placeholders.input</code>
                ), to validate the response against the input.
                <br />
                Examples:
                <code class="ml-1 text-xs">result.response.includes("xxx")</code>
                ,
                <code class="ml-1 text-xs">JSON.parse(result.response).section_ids.includes("abc")</code>
                ,
                <code class="ml-1 text-xs">result.response.includes(placeholders.input)</code>
              </p>
              <div
                v-if="scoreExpressionError"
                class="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                Syntax Error: {{ scoreExpressionError }}
              </div>
            </div>

            <div class="space-y-2">
              <div class="flex gap-4">
                <div class="flex-1">
                  <ComponentTextarea
                    v-model="redFlagExpression"
                    label="Red Flag Expression (JS)"
                    @blur="testRedFlagExpression"
                  />
                </div>
              </div>
              <p class="text-xs text-gray-500">
                Return a truthy value to flag a run. Available:
                <code>result</code>
                and
                <code>placeholders</code>
                (same as score).
                <br />
                Examples:
                <code class="ml-1 text-xs">return /UNSAFE/i.test(result.response);</code>
                ,
                <code class="ml-1 text-xs">return !result.response.includes(placeholders.input);</code>
              </p>
              <div
                v-if="redFlagExpressionError"
                class="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                Syntax Error: {{ redFlagExpressionError }}
              </div>
            </div>

            <div class="space-y-2">
              <div class="flex gap-4">
                <div class="flex-1">
                  <ComponentTextarea
                    v-model="yellowFlagExpression"
                    label="Yellow Flag Expression (JS)"
                    @blur="testYellowFlagExpression"
                  />
                </div>
              </div>
              <p class="text-xs text-gray-500">
                Return a truthy value to mark a run as warning. Available:
                <code>result</code>
                and
                <code>placeholders</code>
                (same as score).
                <br />
                Examples:
                <code class="ml-1 text-xs">return result.usage?.completion_tokens > 5000;</code>
              </p>
              <div
                v-if="yellowFlagExpressionError"
                class="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-700"
              >
                Syntax Error: {{ yellowFlagExpressionError }}
              </div>
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Run Preview + Actions -->
        <MedCard variant="flat" class="sticky bottom-4 z-10">
          <MedCardContent class="flex items-center gap-3">
            <MedButton variant="primary" :disabled="!canStart" :is-loading="isRunning" @click="startMassRun">
              <ComponentIcon class="mr-1 text-sm">rocket_launch</ComponentIcon>
              Start Mass Run ({{ runMatrix.length }} jobs)
            </MedButton>

            <MedButton v-if="isRunning" variant="destructive" @click="abortAll">Abort All</MedButton>

            <MedButton v-if="runs.length > 0 && !isRunning" variant="secondary" size="sm" @click="exportResults">
              <ComponentIcon class="mr-1 text-sm">download</ComponentIcon>
              Export TSV
            </MedButton>

            <div class="ml-auto flex items-center gap-4 text-sm">
              <template v-if="runs.length > 0">
                <span>
                  {{ completedCount }}/{{ totalCount }}
                  <span v-if="passedCount > 0" class="ml-1 text-green-600">{{ passedCount }} ✓</span>
                  <span v-if="failedScoreCount > 0" class="ml-1 text-red-600">{{ failedScoreCount }} ✗</span>
                  <span v-if="redFlagCount > 0" class="ml-1 text-orange-600">{{ redFlagCount }} ⚑</span>
                  <span v-if="yellowFlagCount > 0" class="ml-1 text-yellow-600">{{ yellowFlagCount }} ⚐</span>
                </span>
              </template>
              <span>⏱ {{ durationDisplay }}</span>
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Progress bar (stacked) -->
        <div v-if="runs.length > 0" class="space-y-1">
          <div class="relative flex h-3 overflow-hidden rounded-full bg-gray-200">
            <div
              v-for="segment in progressSegments"
              :key="segment.key"
              class="h-full transition-all duration-300"
              :class="segment.color"
              :style="{width: segment.percent + '%'}"
            />
            <span class="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700">
              {{ completedCount }}/{{ totalCount }}
            </span>
          </div>
          <div class="flex flex-wrap gap-3 text-[10px] text-gray-500">
            <span v-for="segment in progressSegments" :key="segment.key" class="flex items-center gap-1">
              <span class="inline-block size-2 rounded-full" :class="segment.color" />
              {{ segment.label }}: {{ segment.count }}
            </span>
          </div>
        </div>

        <div v-if="globalError" class="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">
          <strong>Error:</strong>
          {{ globalError }}
        </div>

        <MedCard v-if="runs.length > 0">
          <MedCardHeader>Overview</MedCardHeader>
          <MedCardContent class="space-y-4">
            <div class="grid gap-4 lg:grid-cols-2">
              <div class="rounded-lg border border-green-200 bg-green-50 p-4">
                <h3 class="mb-2 text-sm font-semibold text-green-900">Best model / parameter set</h3>
                <template v-if="bestConfiguration">
                  <div class="space-y-1 text-sm text-green-950">
                    <div>
                      <span class="font-semibold">{{ bestConfiguration.provider }}/{{ bestConfiguration.model }}</span>
                    </div>
                    <div>
                      Parameter Set:
                      <span class="font-medium">{{ bestConfiguration.paramSetLabel }}</span>
                    </div>
                    <div>
                      Mean score across {{ bestConfiguration.numericScoreCount }}/{{ bestConfiguration.totalRuns }}
                      iterations:
                      <span class="font-semibold">{{ formatScore(bestConfiguration.meanScore) }}</span>
                      <template v-if="bestConfiguration.scorePercent != null">
                        (
                        <span class="font-semibold">{{ formatScorePercent(bestConfiguration.scorePercent) }}</span>
                        )
                      </template>
                    </div>
                    <div>
                      Min / Max:
                      <span class="font-semibold">{{ formatScore(bestConfiguration.minScore) }}</span>
                      /
                      <span class="font-semibold">{{ formatScore(bestConfiguration.maxScore) }}</span>
                    </div>
                  </div>
                </template>
                <p v-else class="text-sm text-green-900/70">No scorable results available yet.</p>
              </div>

              <div class="rounded-lg border border-red-200 bg-red-50 p-4">
                <h3 class="mb-2 text-sm font-semibold text-red-900">Models that did not work at all</h3>
                <template v-if="completelyBrokenModels.length > 0">
                  <div class="space-y-2 text-sm text-red-950">
                    <div
                      v-for="brokenModel in completelyBrokenModels"
                      :key="brokenModel.key"
                      class="rounded border border-red-200 bg-white/60 px-3 py-2"
                    >
                      <div class="font-semibold">{{ brokenModel.provider }}/{{ brokenModel.model }}</div>
                      <div class="text-xs text-red-800">
                        {{ brokenModel.failedRuns }} failed · {{ brokenModel.abortedRuns }} aborted ·
                        {{ brokenModel.completedRuns }} completed
                      </div>
                    </div>
                  </div>
                </template>
                <p v-else class="text-sm text-red-900/70">
                  All selected models have achieved at least one successful completion run.
                </p>
              </div>
            </div>

            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-gray-200 bg-gray-50 text-left">
                    <th class="px-3 py-2 font-medium">Provider</th>
                    <th class="px-3 py-2 font-medium">Model</th>
                    <th class="px-3 py-2 font-medium">Parameter Set</th>
                    <th class="px-3 py-2 font-medium">Mean</th>
                    <th class="px-3 py-2 font-medium">Min</th>
                    <th class="px-3 py-2 font-medium">Max</th>
                    <th v-if="normalizedMaxScore" class="px-3 py-2 font-medium">%</th>
                    <th class="px-3 py-2 font-medium">Mean Duration</th>
                    <th class="px-3 py-2 font-medium">⚑ Red Flags</th>
                    <th class="px-3 py-2 font-medium">⚐ Yellow Flags</th>
                    <th class="px-3 py-2 font-medium">Completed</th>
                    <th class="px-3 py-2 font-medium">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="summary in configurationSummaries"
                    :key="summary.key"
                    class="border-b border-gray-100"
                    :class="summary.key === bestConfiguration?.key ? 'bg-green-50' : ''"
                  >
                    <td class="px-3 py-2">{{ summary.provider }}</td>
                    <td class="px-3 py-2 font-mono text-xs">{{ summary.model }}</td>
                    <td class="px-3 py-2">{{ summary.paramSetLabel }}</td>
                    <td class="px-3 py-2 font-semibold">{{ formatScore(summary.meanScore) }}</td>
                    <td class="px-3 py-2 text-gray-600">{{ formatScore(summary.minScore) }}</td>
                    <td class="px-3 py-2 text-gray-600">{{ formatScore(summary.maxScore) }}</td>
                    <td v-if="normalizedMaxScore" class="px-3 py-2 font-semibold">
                      {{ formatScorePercent(summary.scorePercent) }}
                    </td>
                    <td class="px-3 py-2 text-xs">{{ formatDuration(summary.meanDurationMs) }}</td>
                    <td
                      class="px-3 py-2"
                      :class="summary.redFlaggedRuns > 0 ? 'font-semibold text-orange-600' : 'text-gray-400'"
                    >
                      {{
                        summary.redFlaggedRuns > 0
                          ? Math.round(summary.redFlagPercent) +
                            "% (" +
                            summary.redFlaggedRuns +
                            "/" +
                            summary.completedRuns +
                            ")"
                          : "–"
                      }}
                    </td>
                    <td
                      class="px-3 py-2"
                      :class="summary.yellowFlaggedRuns > 0 ? 'font-semibold text-yellow-600' : 'text-gray-400'"
                    >
                      {{
                        summary.yellowFlaggedRuns > 0
                          ? Math.round(summary.yellowFlagPercent) +
                            "% (" +
                            summary.yellowFlaggedRuns +
                            "/" +
                            summary.completedRuns +
                            ")"
                          : "–"
                      }}
                    </td>
                    <td class="px-3 py-2">{{ summary.completedRuns }}/{{ summary.totalRuns }}</td>
                    <td class="px-3 py-2">{{ summary.failedRuns }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Results Table -->
        <MedCard v-if="runs.length > 0">
          <MedCardHeader>
            Results
            <span class="ml-2 text-sm font-normal text-gray-500">({{ runs.length }} runs)</span>
          </MedCardHeader>
          <MedCardContent class="overflow-x-auto p-0!">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 bg-gray-50 text-left">
                  <th class="px-3 py-2 font-medium">#</th>
                  <th class="px-3 py-2 font-medium">Provider</th>
                  <th class="px-3 py-2 font-medium">Model</th>
                  <th class="px-3 py-2 font-medium">Param Set</th>
                  <th class="px-3 py-2 font-medium">Iter</th>
                  <th class="px-3 py-2 font-medium">Status</th>
                  <th class="px-3 py-2 font-medium">Duration</th>
                  <th class="px-3 py-2 font-medium">Score</th>
                  <th class="px-3 py-2 font-medium">Red Flags</th>
                  <th class="px-3 py-2 font-medium">Yellow Flags</th>
                  <th class="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(run, rIdx) in runs"
                  :key="rIdx"
                  class="cursor-pointer border-b border-gray-100 transition-colors hover:bg-blue-50"
                  :class="{'bg-blue-50': selectedRunIndex === rIdx}"
                  @click="selectedRunIndex = selectedRunIndex === rIdx ? null : rIdx"
                >
                  <td class="px-3 py-2 font-mono text-xs text-gray-400">{{ rIdx + 1 }}</td>
                  <td class="px-3 py-2">{{ run.provider }}</td>
                  <td class="px-3 py-2 font-mono text-xs">{{ run.model }}</td>
                  <td class="px-3 py-2">
                    {{ run.paramSetLabel }}
                    <ComponentIcon
                      v-if="run.warnings && run.warnings.length > 0"
                      class="ml-1 inline text-sm text-amber-500"
                      :title="run.warnings.join('\n')"
                    >
                      warning
                    </ComponentIcon>
                  </td>
                  <td class="px-3 py-2 text-center">{{ run.iteration }}</td>
                  <td class="px-3 py-2">
                    <AiStatusBadge :status="run.status" />
                  </td>
                  <td class="px-3 py-2 text-xs">{{ formatDuration(run.durationMs) }}</td>
                  <td class="px-3 py-2">
                    <span
                      v-if="run.score != null"
                      class="inline-block rounded px-2 py-0.5 text-xs font-semibold"
                      :class="scoreBadgeClass(run.score)"
                    >
                      {{ scoreDisplay(run.score) }}
                    </span>
                    <span v-else class="text-xs text-gray-400">–</span>
                  </td>
                  <td class="px-3 py-2">
                    <span
                      v-if="run.redFlags === true"
                      class="inline-block rounded bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700"
                    >
                      ⚑
                    </span>
                    <span v-else class="text-xs text-gray-400">–</span>
                  </td>
                  <td class="px-3 py-2">
                    <span
                      v-if="run.yellowFlags === true"
                      class="inline-block rounded bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-700"
                    >
                      ⚐
                    </span>
                    <span v-else class="text-xs text-gray-400">–</span>
                  </td>
                  <td class="px-3 py-2">
                    <MedButton v-if="run.result" variant="ghost" size="sm" @click.stop="selectedRunIndex = rIdx">
                      <ComponentIcon class="text-sm">visibility</ComponentIcon>
                    </MedButton>
                  </td>
                </tr>
              </tbody>
            </table>
          </MedCardContent>
        </MedCard>

        <!-- Detail Panel for selected run -->
        <AiResultCard
          v-if="selectedRun && selectedRun.result"
          :result="selectedRun.result"
          :header="`Detail: #${selectedRunIndex + 1} - ${selectedRun.provider}/${selectedRun.model} (${selectedRun.paramSetLabel}, Iter ${selectedRun.iteration})`"
        >
          <template #header-actions>
            <MedButton variant="ghost" size="sm" @click="selectedRunIndex = null">
              <ComponentIcon class="text-sm">close</ComponentIcon>
            </MedButton>
          </template>
          <template #before-tabs>
            <!-- Warnings -->
            <div
              v-if="selectedRun.warnings && selectedRun.warnings.length > 0"
              class="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            >
              <strong>⚠ Compatibility Warnings:</strong>
              <ul class="mt-1 list-disc pl-4">
                <li v-for="(w, wIdx) in selectedRun.warnings" :key="wIdx">{{ w }}</li>
              </ul>
            </div>

            <!-- Error -->
            <div
              v-if="selectedRun.error"
              class="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              <strong>Error:</strong>
              {{ selectedRun.error }}
            </div>
          </template>
        </AiResultCard>

        <!-- Detail Panel: Error only (no result) -->
        <MedCard v-else-if="selectedRun && selectedRun.error">
          <MedCardHeader class="flex items-center justify-between">
            <span>Detail: #{{ selectedRunIndex + 1 }} - {{ selectedRun.provider }}/{{ selectedRun.model }}</span>
            <MedButton variant="ghost" size="sm" @click="selectedRunIndex = null">
              <ComponentIcon class="text-sm">close</ComponentIcon>
            </MedButton>
          </MedCardHeader>
          <MedCardContent>
            <div class="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <strong>Error:</strong>
              {{ selectedRun.error }}
            </div>
          </MedCardContent>
        </MedCard>
      </div>
    </div>
  </div>
</template>

<style scoped>
  pre {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
</style>
